import { useState, useEffect, useContext } from 'react';
import {
    Box,
    Grid,
    ButtonGroup,
    Button,
    IconButton,
    Tabs,
    Tab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    format,
    addDays,
    addWeeks,
    subWeeks,
    startOfWeek,
    parseISO,
    getDay,
} from 'date-fns';
import SettingsIcon from '@mui/icons-material/Settings';
import CachedIcon from '@mui/icons-material/Cached';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFormSelect,
    SttMenu,
    SttButtonDate,
} from '../../../sporttia/all';
import { getDateLocale } from '../../../lib/dateLocale';

/**
 * Timetable controls
 * ==================
 */
type Props = {
    sc: any;
    date: any;
    loadTimetable: any;
    onChange: any;
    facilityGroupId: any;
    coeffs: any;
    isWeekly: any;
    toggleWeekly: any;
};

export default function TimetableControls({
    sc,
    date,
    loadTimetable,
    onChange, // onChange({facilityGroups, date, coeff})
    facilityGroupId,
    coeffs,
    isWeekly,
}: Props) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    // const [coeff, setCoeff] = useState(0);
    const [facilityGroups, setFacilityGroups] = useState<any>();
    const [weekdayTabValue, setWeekTabValue] = useState(0);
    const theme = useTheme();
    const xsDown = useMediaQuery(theme.breakpoints.down('xs'));

    // Helper function to format weekday with date-fns using correct locale
    const formatWeekday = (weekdayIndex: number, isCompact: boolean) => {
        const dateObj =
            typeof date === 'string' ? parseISO(date) : new Date(date);
        const weekStart = startOfWeek(dateObj, { weekStartsOn: 1 }); // Monday = 1
        const dayDate = addDays(weekStart, weekdayIndex);
        // 'd' = day number, 'EEE' = abbreviated day name (Mon, Tue), 'EEEEEE' = very short (Mo, Tu)
        return format(dayDate, isCompact ? 'd EEEEEE' : 'd EEE', {
            locale: getDateLocale(),
        });
    };

    /**
     * Load facility groups
     */
    const loadFacilityGroups = () => {
        // Guard against undefined sc.id
        if (!sc?.id) return;

        cxt.api('GET', `/scs/${sc.id}/facilitysets`, {
            params: { rows: 1000, status: 'ACTIVE' },
            success: (r: any) => {
                const filteredEmptyFacilities = { count: 0, rows: [] };
                filteredEmptyFacilities.rows = r.rows.filter(
                    (group: any) => group.facilities.count > 0,
                );
                filteredEmptyFacilities.count =
                    filteredEmptyFacilities.rows.length;
                const groupList = filteredEmptyFacilities.rows.map(
                    (group: any) => ({
                        value: group.id,
                        caption: group.name,
                    }),
                );

                setFacilityGroups([
                    {
                        value: -1,
                        caption: t('general.all'),
                    },
                    ...groupList,
                ]);

                // Only set initial facility group if not already set (e.g., from URL)
                if (facilityGroupId === undefined) {
                    onChange({
                        facilityGroupId: r.rows.length > 1 ? r.rows[0].id : -1,
                    });
                }
            },
        });
    };

    /**
     * Get menu items
     */
    const getMenuItems = () => {
        // Init items
        let items: any[] = [];

        // Add coeffs
        items = [
            ...items,
            ...coeffs.map((coeff: any) => ({
                caption: coeff.name,
                onClick: () => onChange({ coeff: coeff.id }),
                divider: false,
            })),
        ];

        return items;
    };

    /**
     * On change weekday tab
     */
    const onChangeWeekday = (newWeekdayTabValue: number) => {
        // Change date
        // onChange({date: weekdays[newWeekdayTabValue].ymd})
        const dateObj =
            typeof date === 'string' ? parseISO(date) : new Date(date);
        const weekStart = startOfWeek(dateObj, { weekStartsOn: 1 });
        const newDate = addDays(weekStart, newWeekdayTabValue);
        onChange({
            date: format(newDate, 'yyyy-MM-dd'),
        });
    };

    /**
     * Date changes
     */
    useEffect(() => {
        const dateObj =
            typeof date === 'string' ? parseISO(date) : new Date(date);
        // getDay returns 0=Sunday, 1=Monday, etc.
        // We need to convert to Monday=0, Tuesday=1, etc. (weekStartsOn: 1)
        const dayOfWeek = getDay(dateObj);
        const mondayBasedWeekday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        setWeekTabValue(mondayBasedWeekday);
    }, [date]);

    /**
     * Init - Load facility groups when sc.id is available
     */
    useEffect(() => {
        if (sc?.id) {
            loadFacilityGroups();
        }
    }, [sc?.id]);

    // coeffs[coeff].width
    return (
        <Box mb={2}>
            <Grid container spacing={3}>
                <Grid size={{ sm: 4, xs: 12 }}>
                    {facilityGroups && (
                        <SttFormSelect
                            defVal={facilityGroupId}
                            onChange={({ value }: any) =>
                                onChange({ facilityGroupId: value })
                            }
                            options={facilityGroups}
                        />
                    )}
                </Grid>

                <Grid sx={{ textAlign: 'center' }} size={{ sm: 7, xs: 12 }}>
                    <ButtonGroup>
                        <Button onClick={() => loadTimetable()}>
                            <CachedIcon />
                        </Button>
                        <Button
                            onClick={() =>
                                onChange({
                                    date: format(new Date(), 'yyyy-MM-dd'),
                                })
                            }
                        >
                            <RotateLeftIcon />
                            &nbsp;
                            {t('general.today')}
                        </Button>
                        <SttButtonDate
                            date={date}
                            onSelectDate={(dated) => onChange({ date: dated })}
                        />
                    </ButtonGroup>
                </Grid>

                <Grid
                    style={xsDown ? { textAlign: 'right' } : {}}
                    size={{ sm: 1, xs: 12 }}
                >
                    <SttMenu icon=<SettingsIcon /> items={getMenuItems()} />
                </Grid>
            </Grid>

            {!isWeekly && (
                <Grid container spacing={3}>
                    <Grid size={{ md: 1, xs: 2 }}>
                        <IconButton
                            onClick={() => {
                                const dateObj =
                                    typeof date === 'string'
                                        ? parseISO(date)
                                        : new Date(date);
                                onChange({
                                    date: format(
                                        subWeeks(dateObj, 1),
                                        'yyyy-MM-dd',
                                    ),
                                });
                            }}
                        >
                            <ArrowBackIosIcon />
                        </IconButton>
                    </Grid>

                    <Grid size={{ md: 10, xs: 8 }}>
                        <Tabs
                            indicatorColor="primary"
                            variant="fullWidth"
                            textColor="primary"
                            value={weekdayTabValue}
                            onChange={(_ev, value) => onChangeWeekday(value)}
                        >
                            {[0, 1, 2, 3, 4, 5, 6].map((wd: number) => (
                                <Tab
                                    style={{ minWidth: 100 }}
                                    key={wd}
                                    label={formatWeekday(
                                        wd,
                                        window.innerWidth <= 760,
                                    )}
                                />
                            ))}
                        </Tabs>
                    </Grid>

                    <Grid size={{ md: 1, xs: 2 }}>
                        <IconButton
                            onClick={() => {
                                const dateObj =
                                    typeof date === 'string'
                                        ? parseISO(date)
                                        : new Date(date);
                                onChange({
                                    date: format(
                                        addWeeks(dateObj, 1),
                                        'yyyy-MM-dd',
                                    ),
                                });
                            }}
                        >
                            <ArrowForwardIosIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}
